/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import appStore from "../../stores/appStore";
import { observer } from "mobx-react-lite";
import categoryPageStore from "./store/categoryPageStore";
import ProductCard from "../../components/ProductCard";
import { Spinner } from "../../components/ui/Spinner";
import { isMobile } from "react-device-detect";
import ToggleSwitch from "../../components/ui/ToggleSwitch";
import { toJS } from "mobx";
import { Helmet } from "react-helmet-async";
import CategoryListPageFilters from "./components/CategoryListPageFilters";
import { Select } from "../../components/ui/Select";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CategoryListPage: FC = () => {
  const { refKey } = useParams<{ refKey: string }>();
  const path = useMemo(
    () => appStore.findCategoryPath(appStore.categories, refKey || ""),
    [appStore.categories, refKey]
  );

  const handleToggleAvailableCity = () => {
    categoryPageStore.toggleAvailableInCity();
  };

  useEffect(() => {
    if (categoryPageStore.isAvailableInCity) {
      categoryPageStore.getAvailables();
    } else {
      if (refKey) {
        categoryPageStore.getProductsByCategory(
          refKey,
          categoryPageStore.products?.current_page
        );
      }
    }
  }, [toJS(categoryPageStore.isAvailableInCity)]);

  useEffect(() => {
    if (refKey) {
      categoryPageStore.getProductsByCategory(refKey);
    }
  }, [refKey]);

  const handlePageChange = (page: number) => {
    if (refKey) {
      categoryPageStore.getProductsByCategory(refKey, page);
    }
  };

  const [pageInput, setPageInput] = useState("");

  const renderPagination = () => {
    if (
      !categoryPageStore.products ||
      categoryPageStore.products.last_page <= 1
    ) {
      return null;
    }

    const { current_page, last_page } = categoryPageStore.products;

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Разрешаем только цифры
      if (value === "" || /^\d+$/.test(value)) {
        setPageInput(value);
      }
    };

    const handlePageInputSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const pageNumber = parseInt(pageInput, 10);
      if (pageNumber >= 1 && pageNumber <= last_page) {
        handlePageChange(pageNumber);
        setPageInput("");
      }
    };

    const handlePageInputBlur = () => {
      const pageNumber = parseInt(pageInput, 10);
      if (pageInput && pageNumber >= 1 && pageNumber <= last_page) {
        handlePageChange(pageNumber);
      }
      setPageInput("");
    };

    return (
      <div className="flex justify-center gap-[25px] mt-[12px] h-[30px]">
        <button className="cursor-pointer !p-0"
          onClick={() => handlePageChange(current_page - 1)}
          disabled={current_page === 1}
        >
          <ChevronLeft className={current_page === 1 ? 'text-gray-400' : 'text-[#232323]'}></ChevronLeft>
        </button>

        <form
          onSubmit={handlePageInputSubmit}
          className="flex items-center gap-[5px] font-medium text-[#232323]"
        >
          <input
            type="text"
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
            placeholder={current_page.toString()}
            className="!w-[22px] text-center  bg-white !rounded-[6px] !h-[30px] border focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-[#232323]">из {last_page}</span>
        </form>
        <button 
        className="cursor-pointer !p-0"
          onClick={() => handlePageChange(current_page + 1)}
          disabled={current_page === last_page}
        >
          <ChevronRight className={current_page === last_page ? 'text-gray-400' : 'text-[#232323]'}></ChevronRight>
        </button>
      </div>
    );
  };

  // if (categoryPageStore.isLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-64">
  //       <Spinner size="lg" />
  //     </div>
  //   );
  // }

  if (!categoryPageStore.isLoading && !categoryPageStore.products) {
    return (
      <div className={isMobile ? "p-4" : "p-0"}>
        <h1>Товары не найдены</h1>
      </div>
    );
  }

  return (
    <div
      className={
        !isMobile
          ? "flex flex-row p-4 gap-4"
          : "flex flex-col p-0 justify-center items-center mt-2"
      }
    >
      <Helmet>
        <>
          <title>
            КЦ Авто | Каталог | {path?.map((i) => i.name).join(" | ")}
          </title>
          <meta
            name="description"
            content={`КЦ авто Каталог запчастей ${path
              ?.map((i) => i.name)
              .join(" | ")}`}
          />
          <meta
            name="keywords"
            content={`КЦ Авто, интернет магазин автозапчастей, поиск запчастей, купить автозапчасти, каталог запчастей, ${path
              ?.map((i) => i.name)
              .join("купить, ")} купить`}
          />
        </>
      </Helmet>
      <CategoryListPageFilters refKey={refKey || ""}></CategoryListPageFilters>
      {categoryPageStore.isLoading && (
        <div className="flex w-full justify-center items-center h-64">
          <Spinner size="lg" />
        </div>
      )}

      {!categoryPageStore.isLoading && !categoryPageStore.products && (
        <div className="bg-white rounded-[20px] shadow-[0_2px_20px_#0000001a] p-4 flex flex-col">
          <span className=" font-bold text-2xl">
            К сожалению, ничего не найдено
          </span>
        </div>
      )}
      {!categoryPageStore.isLoading && categoryPageStore.products && (
        <div
          className={
            !isMobile
              ? "flex flex-col items-center p-2"
              : "flex flex-col items-center"
          }
        >
          <div className="flex flex-row justify-between items-center mb-2 w-full">
            <nav
              className={`flex items-start text-left w-full text-[16px] text-[#333333] ${
                isMobile ? "p-4" : "mb-4"
              }`}
            >
              {path?.map((category, index) => (
                <div key={category.ref_key} className="flex items-center">
                  {index > 0 && <span className="mx-2 text-[#333333] text-[16px]"> - </span>}
                  {index === path.length - 1 ? (
                    <span className="text-[#333333] font-semibold text-[16px]">
                      {category.name}
                    </span>
                  ) : (
                    <Link
                      to={`/category/${category.ref_key}`}
                      className="hover:text-blue-500 hover:underline text-[16px] font-semibold text-[#333333]"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            {!isMobile && (() => {
              const sortOptions = [
                { value: undefined, label: "Сортировка" },
                { value: "price:asc", label: "По возрастанию цены" },
                { value: "price:desc", label: "По убыванию цены" },
                { value: "name:asc", label: "По наименованию A-Z/А-Я" },
                { value: "name:desc", label: "По наименованию Z-A/Я-А" },
              ];
              const sortValue =
                categoryPageStore.sortBy && categoryPageStore.sortDirection
                  ? `${categoryPageStore.sortBy}:${categoryPageStore.sortDirection}`
                  : "";
              return (
                <Select
                  options={sortOptions}
                  value={sortValue}
                  onChange={async (val) => {
                    const [by, dir] = String(val).split(":");
                    categoryPageStore.setSort(
                      by as "name" | "price" | "",
                      dir as "asc" | "desc" | ""
                    );
                    await categoryPageStore.getProductsByCategory(refKey || "", categoryPageStore.products?.current_page);
                  }}
                  placeholder="Сортировка"
                  disabled={categoryPageStore.isLoading}
                  className="min-w-[200px] h-[35px] !cursor-pointer"
                />
              );
            })()}
          </div>

          <div
          // className={
          //   isMobile
          //     ? "bg-white rounded-2xl w-full"
          //     : "bg-white rounded-2xl w-[1000px]"
          // }
          >
            {(() => {
              // Разделяем товары на две группы
              const currentCityProducts =
                categoryPageStore.products?.data.filter(
                  (product) => product.availableCurrentCity
                ) || [];
              const otherCitiesProducts =
                categoryPageStore.products?.data.filter(
                  (product) => !product.availableCurrentCity
                ) || [];

              const hasCurrentCity = currentCityProducts.length > 0;
              const hasOtherCities = otherCitiesProducts.length > 0;
              const shouldDivide = hasCurrentCity && hasOtherCities;

              // Функция для рендера сетки товаров
              const renderProductGrid = (
                products: typeof categoryPageStore.products.data,
                startIndex: number = 0
              ) => {
                const totalItems = products.length;
                const itemsPerRow = {
                  base: 1,
                  sm: 2,
                  md: 3,
                  lg: 4,
                };

                return products.map((product, index) => {
                  const globalIndex = startIndex + index;

                  // Определяем позицию элемента
                  const isFirstInRow = {
                    base: index % itemsPerRow.base === 0,
                    sm: index % itemsPerRow.sm === 0,
                    md: index % itemsPerRow.md === 0,
                    lg: index % itemsPerRow.lg === 0,
                  };

                  const isLastInRow = {
                    base: (index + 1) % itemsPerRow.base === 0,
                    sm: (index + 1) % itemsPerRow.sm === 0,
                    md: (index + 1) % itemsPerRow.md === 0,
                    lg: (index + 1) % itemsPerRow.lg === 0,
                  };

                  const isFirstRow = index < itemsPerRow.lg;
                  const isLastRow =
                    index >=
                    totalItems -
                      (totalItems % itemsPerRow.lg || itemsPerRow.lg);

                  // Формируем классы для границ
                  const borderClasses = [
                    "border-[#54545657] border-b-[1px]",
                    // Нижняя граница для всех элементов, кроме последней строки
                    isFirstRow ? "" : "",
                    isLastRow ? "!border-b-[0px]" : "",
                    // Левая граница убирается только для первого в строке
                    isFirstInRow.base ? "!border-l-0" : "border-l-[1px]",
                    // Правая граница убирается только для последнего в строке
                    isLastInRow.base ? "border-r-0" : "border-r-[1px]",
                    // Для sm экранов
                    "",
                    isFirstInRow.sm ? "sm:border-l-0" : "sm:border-l-[1px]",
                    isLastInRow.sm ? "sm:border-r-0" : "sm:border-r-[1px]",
                    // Для md экранов
                    "",
                    isFirstInRow.md ? "md:border-l-0" : "md:border-l-[1px]",
                    isLastInRow.md ? "md:border-r-0" : "md:border-r-[1px]",
                    // Для lg экранов
                    "",
                    isFirstInRow.lg
                      ? "!border-l-0 !rounded-lt-[20px]"
                      : "lg:border-l-[1px]",
                    isLastInRow.lg
                      ? "!border-r-0 !rounded-rt-[20px]"
                      : "lg:border-r-[1px]",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <ProductCard
                      key={product.ref_key}
                      className={borderClasses}
                      product={product}
                    />
                  );
                });
              };

              return (
                <>
                  {shouldDivide ? (
                    <>
                      {/* Товары в текущем городе */}
                      <div
                        className={
                          "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 rounded-2xl bg-white " +
                          (isMobile
                            ? "bg-white rounded-2xl w-full"
                            : "bg-white rounded-[20px] w-[1000px] p-[6px] shadow-[0_2px_20px_#0000001a]")
                        }
                      >
                        {renderProductGrid(currentCityProducts, 0)}
                      </div>

                      {/* Разделитель */}
                      <div
                        className={
                          !isMobile
                            ? "my-6 px-4 "
                            : "mt-[20px]  pr-[30px] pl-[10px] self-start"
                        }
                      >
                        <h2
                          className={
                            !isMobile
                              ? "text-[1.68rem] font-bold text-[#232323] tracking-[-0.02em]"
                              : "text-[17px] font-bold text-[#232323] text-left tracking-[-0.02em]"
                          }
                        >
                          В наличии в других городах
                        </h2>
                      </div>

                      {/* Товары в других городах */}
                      <div
                        className={
                          "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 rounded-2xl " +
                          (isMobile
                            ? "bg-white rounded-2xl w-full"
                            : "bg-white rounded-[20px] w-[1000px] p-[6px] shadow-[0_2px_20px_#0000001a]")
                        }
                      >
                        {renderProductGrid(
                          otherCitiesProducts,
                          currentCityProducts.length
                        )}
                      </div>
                    </>
                  ) : (
                    // Если только одна группа - отображаем как раньше
                    <div
                      className={
                        "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 rounded-2xl " +
                        (isMobile
                          ? "bg-white rounded-2xl w-full"
                          : "bg-white rounded-[20px] w-[1000px] p-[6px] shadow-[0_2px_20px_#0000001a]")
                      }
                    >
                      {renderProductGrid(
                        categoryPageStore.products?.data || [],
                        0
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {renderPagination()}

          <div className="mt-2 text-sm text-gray-500">
            Страница {categoryPageStore.products?.current_page} из{" "}
            {categoryPageStore.products?.last_page}
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(CategoryListPage);
